import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  CustomInput,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import axios from "axios";
import { connect } from "react-redux";

import { END_POINT } from "../../../constants/defaultValues";
import {
  AddCommaNumber,
  NotificatioErrController,
  NotificatioSucController,
} from "../../../helpers/Utils";
import {
  CASH_FLOW_STATUS,
  CASH_FLOW_TYPE,
  RECURRING_MATRIX_ROWS,
  buildCashFlowSummary,
  buildRecurringKpis,
  filterCashFlowItems,
  getCashFlowDate,
  getRecurringCellStatus,
  isRecurringCashFlowItem,
  matchesCashFlowPeriod,
  normalizeCashFlowItem,
  normalizeCashInputValue,
  normalizeRecurringYearData,
  sortCashFlowItems,
} from "../../../helpers/cashFlow";
import LogsHelper from "../../../helpers/Logs";
import { getCashFlowCreateFormConfig } from "../../../helpers/cashFlowForm";
import { isC, isD, isF, isR, isU } from "../../../helpers/Roles";
import createSocket from "../../../helpers/createSocket";
import IntlMessages from "../../../helpers/IntlMessages";
import { setSearchContext } from "../../../redux/actions";
import AddIncome from "../../../containers/pages/income/AddIncome";
import UpdateIncome from "../../../containers/pages/income/UpdateIncome";
import AddOutcome from "../../../containers/pages/outcome/AddOutcome";
import UpdateOutcome from "../../../containers/pages/outcome/UpdateOutcome";

const CASH_FLOW_TYPES = [CASH_FLOW_TYPE.INCOME, CASH_FLOW_TYPE.OUTCOME];
const DEFAULT_PAGE_SIZE = 20;

const DEFAULT_FORM = {
  id: "",
  type: CASH_FLOW_TYPE.INCOME,
  sub_title: "",
  name: "",
  value: "",
  note: "",
  time: "",
  address: "",
  status: "",
  alias: null,
  user: {},
  at: null,
  dynamic_form: {},
  voucherTemplate: "",
};

const EMPTY_SHOW_STATE = {
  addIncome: false,
  addOutcome: false,
  updateIncome: false,
  updateOutcome: false,
};

const CATEGORY_META = {
  "Điện": { color: "#f59e0b", bg: "#fff7ed", icon: "iconsminds-electricity" },
  Internet: { color: "#0ea5e9", bg: "#eff6ff", icon: "iconsminds-wifi" },
  Marketing: { color: "#8b5cf6", bg: "#f5f3ff", icon: "iconsminds-speach-bubble-7" },
  "Nước": { color: "#0ea5e9", bg: "#eef6ff", icon: "iconsminds-drop" },
  "Lương": { color: "#10b981", bg: "#ecfdf5", icon: "iconsminds-business-man" },
  "Mặt bằng": { color: "#f97316", bg: "#fff7ed", icon: "iconsminds-building" },
  "Vận chuyển": { color: "#a855f7", bg: "#faf5ff", icon: "iconsminds-truck" },
  "Chi khác": { color: "#64748b", bg: "#f8fafc", icon: "simple-icon-options" },
  "Thu cọc": { color: "#059669", bg: "#ecfdf5", icon: "iconsminds-coins" },
  "Hoàn tiền": { color: "#0284c7", bg: "#eff6ff", icon: "iconsminds-refresh" },
  "Thu khác": { color: "#16a34a", bg: "#f0fdf4", icon: "iconsminds-add-file" },
};

const TABS = [
  { key: "overview", label: "Tổng quan" },
  { key: "recurring", label: "Định kỳ" },
  { key: CASH_FLOW_TYPE.INCOME, label: "Phiếu thu" },
  { key: CASH_FLOW_TYPE.OUTCOME, label: "Phiếu chi" },
];

const CREATE_VOUCHER_OPTIONS = [
  {
    key: "income",
    label: "Phiếu thu",
    description: "Ghi nhận khoản tiền vào",
    type: CASH_FLOW_TYPE.INCOME,
    icon: "iconsminds-add-file",
    color: "#059669",
    bg: "#ecfdf5",
  },
  {
    key: "outcome",
    label: "Phiếu chi",
    description: "Ghi nhận khoản tiền ra",
    type: CASH_FLOW_TYPE.OUTCOME,
    icon: "iconsminds-remove-file",
    color: "#dc2626",
    bg: "#fef2f2",
  },
  {
    key: "internet",
    label: "Internet",
    description: "Chi phí mạng định kỳ",
    type: CASH_FLOW_TYPE.OUTCOME,
    title: "Chi phí Internet",
    icon: "iconsminds-wifi",
    color: "#0284c7",
    bg: "#eff6ff",
  },
  {
    key: "rent",
    label: "Mặt bằng",
    description: "Tiền thuê địa điểm",
    type: CASH_FLOW_TYPE.OUTCOME,
    title: "Chi phí mặt bằng",
    icon: "iconsminds-building",
    color: "#ea580c",
    bg: "#fff7ed",
  },
  {
    key: "electricity",
    label: "Điện",
    description: "Tiền điện theo kỳ",
    type: CASH_FLOW_TYPE.OUTCOME,
    title: "Tiền điện",
    icon: "iconsminds-electricity",
    color: "#d97706",
    bg: "#fffbeb",
  },
  {
    key: "water",
    label: "Nước",
    description: "Tiền nước theo kỳ",
    type: CASH_FLOW_TYPE.OUTCOME,
    title: "Tiền nước",
    icon: "iconsminds-drop",
    color: "#0891b2",
    bg: "#ecfeff",
  },
  {
    key: "marketing",
    label: "Marketing",
    description: "Quảng cáo và truyền thông",
    type: CASH_FLOW_TYPE.OUTCOME,
    title: "Chi phí Marketing",
    icon: "iconsminds-speach-bubble-7",
    color: "#7c3aed",
    bg: "#f5f3ff",
  },
  {
    key: "other",
    label: "Khác",
    description: "Khoản chi theo tiêu đề riêng",
    type: CASH_FLOW_TYPE.OUTCOME,
    icon: "simple-icon-options",
    color: "#475569",
    bg: "#f8fafc",
    requiresTitle: true,
  },
];

const MONTH_LABELS = [
  "Tháng 1",
  "Tháng 2",
  "Tháng 3",
  "Tháng 4",
  "Tháng 5",
  "Tháng 6",
  "Tháng 7",
  "Tháng 8",
  "Tháng 9",
  "Tháng 10",
  "Tháng 11",
  "Tháng 12",
];

const styles = {
  page: {
    color: "#172554",
    paddingBottom: 48,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: 8,
    marginBottom: 18,
  },
  tabs: {
    display: "flex",
    alignItems: "center",
    gap: 26,
    overflowX: "auto",
    minHeight: 44,
  },
  tab: {
    border: "0 solid transparent",
    borderBottom: "2px solid transparent",
    background: "transparent",
    color: "#172554",
    padding: "10px 0",
    fontWeight: 600,
    whiteSpace: "nowrap",
    cursor: "pointer",
    outline: "none",
    boxShadow: "none",
  },
  activeTab: {
    color: "#0d6efd",
    borderBottom: "2px solid #0d6efd",
  },
  content: {
    display: "flex",
    alignItems: "flex-start",
    gap: 18,
  },
  ledger: {
    flex: "1 1 780px",
    minWidth: 0,
  },
  inspector: {
    flex: "0 0 390px",
    maxWidth: "100%",
    position: "sticky",
    top: 84,
    alignSelf: "flex-start",
  },
  panel: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
  },
  // kpiCard: {
  //   border: "1px solid #e5e7eb",
  //   borderRadius: 8,
  //   boxShadow: "0 10px 28px rgba(15, 23, 42, 0.04)",
  //   minHeight: 112,
  // },
  softButton: {
    border: "1px solid #dbeafe",
    background: "#f8fbff",
    color: "#1d4ed8",
    borderRadius: 6,
    minHeight: 34,
    padding: "5px 10px",
    fontSize: 13,
  },
  filterInput: {
    height: 34,
    minHeight: 34,
    padding: "4px 10px",
    fontSize: 13,
    borderColor: "#e5e7eb",
    borderRadius: 6,
  },
  compactButton: {
    minHeight: 34,
    padding: "5px 10px",
    fontSize: 13,
    borderRadius: 6,
  },
  tableHead: {
    color: "#475569",
    fontSize: 12,
    background: "#fbfdff",
  },
  tableRow: {
    cursor: "pointer",
  },
  selectedRow: {
    background: "#f8fbff",
  },
  section: {
    border: "1px solid #edf2f7",
    borderRadius: 8,
    padding: 18,
    background: "#fff",
  },
};

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
});

const cleanCashValue = (value) =>
  String(value || "0")
    .replace(/\./g, "")
    .replace(/,/g, "");

const formatMoney = (value) => `${AddCommaNumber(Math.round(value || 0)) || 0}đ`;

const canReadType = (type) =>
  isR(type) || isF(type) || isC(type) || isU(type) || isD(type);
const canCreateType = (type) => isC(type) || isF(type);
const canUpdateType = (type) => isU(type) || isF(type);
const canDeleteType = (type) => isD(type) || isF(type);

const getInitialTab = () => {
  if (window.location.pathname.includes("/income")) return CASH_FLOW_TYPE.INCOME;
  if (window.location.pathname.includes("/outcome")) return CASH_FLOW_TYPE.OUTCOME;
  return "overview";
};

const getTypeMeta = (type) => {
  if (type === CASH_FLOW_TYPE.OUTCOME) {
    return {
      label: "Chi",
      fullLabel: "Phiếu chi",
      color: "danger",
      tone: "#ef4444",
      softBg: "#fee2e2",
      icon: "iconsminds-remove-file",
      endpoint: "outcome",
      logCreate: "outcome-create",
      logUpdate: "outcome-update",
      logDelete: "outcome-delete",
    };
  }

  return {
    label: "Thu",
    fullLabel: "Phiếu thu",
    color: "success",
    tone: "#16a34a",
    softBg: "#dcfce7",
    icon: "iconsminds-add-file",
    endpoint: "income",
    logCreate: "income-create",
    logUpdate: "income-update",
    logDelete: "income-delete",
  };
};

const getStatusColor = (status) => {
  if (status === CASH_FLOW_STATUS.APPROVED) return "success";
  if (status === CASH_FLOW_STATUS.PENDING) return "warning";
  if (status === CASH_FLOW_STATUS.REJECTED) return "danger";
  return "secondary";
};

const getStatusLabel = (status) => {
  if (status === CASH_FLOW_STATUS.APPROVED) return "Đã duyệt";
  if (status === CASH_FLOW_STATUS.PENDING) return "Chờ duyệt";
  if (status === CASH_FLOW_STATUS.REJECTED) return "Từ chối";
  return status || "Không rõ";
};

const formatDateTime = (item) => {
  const date = getCashFlowDate(item);
  if (!date) return item?.displayDate || "-";

  return `${date.toLocaleDateString("vi-VN")} ${date.toLocaleTimeString("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const formatDateOnly = (item) => {
  const date = getCashFlowDate(item);
  if (!date) return item?.displayDate || "-";

  return date.toLocaleDateString("vi-VN");
};

const getCreatorName = (item) => {
  if (!item?.user) return "-";
  if (typeof item.user === "string") return item.user || "-";
  return item.user.fullname || item.user.username || "-";
};

const getCategoryMeta = (category) =>
  CATEGORY_META[category] || CATEGORY_META["Chi khác"];

const getTabRecords = (items, activeTab) => {
  if (activeTab === CASH_FLOW_TYPE.INCOME) {
    return items.filter((item) => item.type === CASH_FLOW_TYPE.INCOME);
  }

  if (activeTab === CASH_FLOW_TYPE.OUTCOME) {
    return items.filter((item) => item.type === CASH_FLOW_TYPE.OUTCOME);
  }

  if (activeTab === "recurring") {
    return items.filter(isRecurringCashFlowItem);
  }

  return items;
};

const getVisiblePeriodLabel = (periodFilter) => {
  if (!periodFilter) return "Tất cả kỳ";
  const [year, month, day] = periodFilter.split("-");
  return day ? `${day}/${month}/${year}` : `${month}/${year}`;
};

const formatMonthFilterValue = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const formatDayFilterValue = (date) =>
  `${formatMonthFilterValue(date)}-${String(date.getDate()).padStart(2, "0")}`;

const getFilterDate = (value, fallback = new Date()) => {
  if (!value) return fallback;
  const [year, month, day = "1"] = value.split("-");
  return new Date(Number(year), Number(month) - 1, Number(day));
};

const PeriodFilter = ({ value, onChange }) => {
  const now = new Date();
  const selectedDate = getFilterDate(value, now);
  const [open, setOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState(value.length === 10 ? "day" : "month");
  const [displayYear, setDisplayYear] = useState(selectedDate.getFullYear());
  const [displayMonth, setDisplayMonth] = useState(selectedDate.getMonth());
  const selectedYear = value ? selectedDate.getFullYear() : null;
  const selectedMonth = value ? selectedDate.getMonth() : null;
  const selectedDay = value.length === 10 ? selectedDate.getDate() : null;
  const isDayValue = value.length === 10;

  const toggle = () => {
    if (!open) {
      setDisplayYear(selectedYear || now.getFullYear());
      setDisplayMonth(selectedMonth ?? now.getMonth());
      setPickerMode(isDayValue ? "day" : "month");
    }
    setOpen((current) => !current);
  };

  const movePeriod = (offset) => {
    const baseDate = getFilterDate(value, now);
    if (isDayValue) {
      baseDate.setDate(baseDate.getDate() + offset);
      onChange(formatDayFilterValue(baseDate));
      return;
    }
    baseDate.setMonth(baseDate.getMonth() + offset);
    onChange(formatMonthFilterValue(baseDate));
  };

  const moveCalendar = (offset) => {
    if (pickerMode === "month") {
      setDisplayYear((year) => year + offset);
      return;
    }
    const nextMonth = new Date(displayYear, displayMonth + offset, 1);
    setDisplayYear(nextMonth.getFullYear());
    setDisplayMonth(nextMonth.getMonth());
  };

  const firstWeekday = (new Date(displayYear, displayMonth, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(displayYear, displayMonth + 1, 0).getDate();
  const dayCells = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
  ];

  const selectedLabel = isDayValue
    ? selectedDate.toLocaleDateString("vi-VN")
    : value
      ? `${MONTH_LABELS[selectedMonth]} / ${selectedYear}`
      : "Tất cả thời gian";

  return (
    <div className="d-flex align-items-center" style={{ minWidth: 218 }}>
      <Button
        type="button"
        size="sm"
        color="light"
        title={isDayValue ? "Ngày trước" : "Tháng trước"}
        aria-label={isDayValue ? "Chọn ngày trước" : "Chọn tháng trước"}
        style={{ ...styles.compactButton, width: 34, padding: 0, flex: "0 0 34px" }}
        onClick={() => movePeriod(-1)}
      >
        <i className="simple-icon-arrow-left" />
      </Button>
      <Dropdown isOpen={open} toggle={toggle} className="mx-1 flex-grow-1">
        <DropdownToggle
          tag="button"
          type="button"
          className="btn btn-light btn-sm w-100 d-flex align-items-center justify-content-between"
          style={{ ...styles.compactButton, minWidth: 142, color: "#334155" }}
        >
          <span className="d-flex align-items-center min-width-zero">
            <i className="simple-icon-calendar mr-2 text-primary" />
            <span className="text-truncate">
              {selectedLabel}
            </span>
          </span>
          <i className="simple-icon-arrow-down ml-2 text-muted" style={{ fontSize: 9 }} />
        </DropdownToggle>
        <DropdownMenu className="p-3 shadow" style={{ width: 320, borderRadius: 8 }}>
          <div className="d-flex p-1 mb-3" style={{ background: "#f1f5f9", borderRadius: 6 }}>
            {[{ key: "day", label: "Theo ngày" }, { key: "month", label: "Theo tháng" }].map(
              (mode) => (
                <Button
                  key={mode.key}
                  type="button"
                  size="sm"
                  color={pickerMode === mode.key ? "primary" : "light"}
                  className="flex-fill border-0"
                  style={{ borderRadius: 5, fontSize: 12 }}
                  onClick={(event) => {
                    event.stopPropagation();
                    setPickerMode(mode.key);
                  }}
                >
                  {mode.label}
                </Button>
              ),
            )}
          </div>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <Button
              type="button"
              color="light"
              size="sm"
              title={pickerMode === "day" ? "Tháng trước" : "Năm trước"}
              aria-label={pickerMode === "day" ? "Xem tháng trước" : "Xem năm trước"}
              style={{ ...styles.compactButton, width: 32, padding: 0 }}
              onClick={(event) => {
                event.stopPropagation();
                moveCalendar(-1);
              }}
            >
              <i className="simple-icon-arrow-left" />
            </Button>
            <strong>
              {pickerMode === "day" ? `${MONTH_LABELS[displayMonth]} / ${displayYear}` : displayYear}
            </strong>
            <Button
              type="button"
              color="light"
              size="sm"
              title={pickerMode === "day" ? "Tháng sau" : "Năm sau"}
              aria-label={pickerMode === "day" ? "Xem tháng sau" : "Xem năm sau"}
              style={{ ...styles.compactButton, width: 32, padding: 0 }}
              onClick={(event) => {
                event.stopPropagation();
                moveCalendar(1);
              }}
            >
              <i className="simple-icon-arrow-right" />
            </Button>
          </div>
          {pickerMode === "month" ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 6,
              }}
            >
              {MONTH_LABELS.map((label, monthIndex) => {
                const selected = selectedYear === displayYear && selectedMonth === monthIndex;
                return (
                  <Button
                    key={label}
                    type="button"
                    size="sm"
                    color={selected ? "primary" : "light"}
                    style={{ borderRadius: 6, fontSize: 12, padding: "6px 4px" }}
                    onClick={(event) => {
                      event.stopPropagation();
                      onChange(`${displayYear}-${String(monthIndex + 1).padStart(2, "0")}`);
                      setOpen(false);
                    }}
                  >
                    {label.replace("Tháng ", "T")}
                  </Button>
                );
              })}
            </div>
          ) : (
            <>
              <div
                className="text-muted text-center mb-1"
                style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", fontSize: 11 }}
              >
                {["T2", "T3", "T4", "T5", "T6", "T7", "CN"].map((dayName) => (
                  <span key={dayName}>{dayName}</span>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
                {dayCells.map((day, index) => {
                  const selected =
                    day &&
                    selectedYear === displayYear &&
                    selectedMonth === displayMonth &&
                    selectedDay === day;
                  const today =
                    day &&
                    now.getFullYear() === displayYear &&
                    now.getMonth() === displayMonth &&
                    now.getDate() === day;
                  return day ? (
                    <Button
                      key={`${displayYear}-${displayMonth}-${day}`}
                      type="button"
                      size="sm"
                      color={selected ? "primary" : "light"}
                      title={`${day}/${displayMonth + 1}/${displayYear}`}
                      style={{
                        width: 36,
                        height: 32,
                        padding: 0,
                        borderRadius: 6,
                        fontSize: 12,
                        borderColor: today && !selected ? "#60a5fa" : undefined,
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        onChange(
                          `${displayYear}-${String(displayMonth + 1).padStart(2, "0")}-${String(
                            day,
                          ).padStart(2, "0")}`,
                        );
                        setOpen(false);
                      }}
                    >
                      {day}
                    </Button>
                  ) : (
                    <span key={`empty-${index}`} style={{ width: 36, height: 32 }} />
                  );
                })}
              </div>
            </>
          )}
          <div className="d-flex justify-content-between mt-3 pt-3 border-top">
            <Button
              type="button"
              color="link"
              size="sm"
              className="p-0"
              onClick={(event) => {
                event.stopPropagation();
                onChange("");
                setOpen(false);
              }}
            >
              Tất cả
            </Button>
            <div>
              {pickerMode === "day" ? (
                <Button
                  type="button"
                  color="link"
                  size="sm"
                  className="p-0 mr-3"
                  onClick={(event) => {
                    event.stopPropagation();
                    onChange(`${displayYear}-${String(displayMonth + 1).padStart(2, "0")}`);
                    setPickerMode("month");
                    setOpen(false);
                  }}
                >
                  Cả tháng
                </Button>
              ) : null}
              <Button
                type="button"
                color="link"
                size="sm"
                className="p-0 font-weight-bold"
                onClick={(event) => {
                  event.stopPropagation();
                  onChange(
                    pickerMode === "day"
                      ? formatDayFilterValue(now)
                      : formatMonthFilterValue(now),
                  );
                  setOpen(false);
                }}
              >
                {pickerMode === "day" ? "Hôm nay" : "Tháng này"}
              </Button>
            </div>
          </div>
        </DropdownMenu>
      </Dropdown>
      <Button
        type="button"
        size="sm"
        color="light"
        title={isDayValue ? "Ngày sau" : "Tháng sau"}
        aria-label={isDayValue ? "Chọn ngày sau" : "Chọn tháng sau"}
        style={{ ...styles.compactButton, width: 34, padding: 0, flex: "0 0 34px" }}
        onClick={() => movePeriod(1)}
      >
        <i className="simple-icon-arrow-right" />
      </Button>
    </div>
  );
};

const CashFlowKpi = ({ label, value, hint, color, icon }) => (
  <Card className="mb-3" style={styles.kpiCard}>
    <CardBody className="d-flex align-items-center">
      <div
        className="d-flex align-items-center justify-content-center mr-3"
        style={{
          width: 58,
          height: 58,
          borderRadius: "50%",
          background: color.bg,
          color: color.text,
          flex: "0 0 58px",
        }}
      >
        <i className={icon} style={{ fontSize: 27 }} />
      </div>
      <div className="min-width-zero">
        <div className="text-muted text-small mb-2">{label}</div>
        <div className="font-weight-bold" style={{ color: color.text, fontSize: 22 }}>
          {value}
        </div>
        <div className="text-muted text-small mt-2">{hint}</div>
      </div>
    </CardBody>
  </Card>
);

const RECURRING_STATUS_META = {
  approved: { label: "Đã duyệt", color: "#22c55e" },
  pending: { label: "Chờ duyệt", color: "#f59e0b" },
  rejected: { label: "Đã từ chối", color: "#ef4444" },
  mixed: { label: "Nhiều trạng thái", color: "#3b82f6" },
  automatic: { label: "Dữ liệu tự động", color: "#94a3b8" },
  empty: { label: "Chưa có dữ liệu", color: "#e2e8f0" },
};

const getRecurringCellAmount = (cell, rowKey, statusFilter) => {
  if (!cell) return 0;
  if (rowKey === "salary") return statusFilter === "all" ? cell.grossTotal : 0;
  if (statusFilter === "all") return cell.grossTotal;
  return cell.statusTotals?.[statusFilter] || 0;
};

const RecurringYearKpi = ({ label, category, data, active, onClick }) => {
  const meta = getCategoryMeta(category);
  const detail = data.staffCount
    ? `Lũy kế · ${data.recordCount} phiếu · ${data.staffCount} nhân viên`
    : `Lũy kế · ${data.recordCount} phiếu`;

  return (
    <Card
      role="button"
      tabIndex="0"
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onClick();
      }}
      // style={{
      //   border: active ? `1px solid ${meta.color}` : "1px solid #e5e7eb",
      //   borderRadius: 8,
      //   cursor: "pointer",
      //   minHeight: 126,
      //   boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
      // }}
    >
      <CardBody className="d-flex flex-column justify-content-between p-3">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center min-width-zero">
            <span
              className="d-flex align-items-center justify-content-center mr-2 flex-shrink-0"
              style={{ width: 34, height: 34, borderRadius: "50%", background: meta.bg, color: meta.color }}
            >
              <i className={meta.icon} style={{ fontSize: 17 }} />
            </span>
            <span className="font-weight-bold text-truncate">{label}</span>
          </div>
          {data.pendingTotal > 0 ? (
            <span className="text-small" style={{ color: "#d97706" }} title="Số tiền chờ duyệt">
              {formatMoney(data.pendingTotal)}
            </span>
          ) : null}
        </div>
        <div>
          <div className="font-weight-bold mt-3" style={{ color: meta.color, fontSize: 20 }}>
            {formatMoney(data.value)}
          </div>
          <div className="text-muted text-small mt-1">{detail}</div>
        </div>
      </CardBody>
    </Card>
  );
};

const RecurringYearView = ({
  data,
  loading,
  selectedPeriod,
  categoryFilter,
  statusFilter,
  onPeriodChange,
  onMovePeriod,
  onCategoryChange,
  onStatusChange,
  onSelectMonth,
}) => {
  const selectedMonth = Number(selectedPeriod.slice(5, 7));
  const kpis = buildRecurringKpis({ categories: data.allTimeCategories });
  const visibleRows = RECURRING_MATRIX_ROWS.filter((row) => {
    if (categoryFilter === "all") return true;
    if (categoryFilter === "otherSummary") {
      return ["marketing", "salary", "other"].includes(row.key);
    }
    return row.key === categoryFilter;
  });
  const kpiItems = [
    { key: "location", label: "Mặt bằng", category: "Mặt bằng" },
    { key: "internet", label: "Internet", category: "Internet" },
    { key: "electricity", label: "Điện", category: "Điện" },
    { key: "water", label: "Nước", category: "Nước" },
    { key: "other", label: "Khác", category: "Chi khác" },
  ];

  return (
    <>
      <div style={styles.panel} className="mb-3 p-3">
        <div className="d-flex flex-wrap align-items-center" style={{ gap: 10 }}>
          <div className="d-flex align-items-center">
            <Button
              size="sm"
              color="light"
              style={{ ...styles.compactButton, width: 34, padding: 0 }}
              title="Tháng trước"
              onClick={() => onMovePeriod(-1)}
            >
              <i className="simple-icon-arrow-left" />
            </Button>
            <Input
              type="month"
              value={selectedPeriod}
              onChange={(event) => event.target.value && onPeriodChange(event.target.value)}
              style={{ ...styles.filterInput, width: 168, margin: "0 6px" }}
              aria-label="Chọn tháng định kỳ"
            />
            <Button
              size="sm"
              color="light"
              style={{ ...styles.compactButton, width: 34, padding: 0 }}
              title="Tháng sau"
              onClick={() => onMovePeriod(1)}
            >
              <i className="simple-icon-arrow-right" />
            </Button>
          </div>

          <Input
            type="select"
            value={categoryFilter}
            onChange={(event) => onCategoryChange(event.target.value)}
            style={{ ...styles.filterInput, width: 180 }}
            aria-label="Lọc danh mục định kỳ"
          >
            <option value="all">Tất cả danh mục</option>
            <option value="otherSummary">Nhóm Khác (KPI)</option>
            {RECURRING_MATRIX_ROWS.map((row) => (
              <option key={row.key} value={row.key}>{row.label}</option>
            ))}
          </Input>

          <Input
            type="select"
            value={statusFilter}
            onChange={(event) => onStatusChange(event.target.value)}
            style={{ ...styles.filterInput, width: 170 }}
            aria-label="Lọc trạng thái định kỳ"
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="approved">Đã duyệt</option>
            <option value="pending">Chờ duyệt</option>
            <option value="rejected">Đã từ chối</option>
          </Input>

          {categoryFilter !== "all" || statusFilter !== "all" ? (
            <Button
              size="sm"
              style={styles.softButton}
              onClick={() => {
                onCategoryChange("all");
                onStatusChange("all");
              }}
            >
              <i className="iconsminds-filter-2 mr-1" />
              Đặt lại
            </Button>
          ) : null}

        </div>
      </div>

      <div
        className="mb-3"
        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}
      >
        {kpiItems.map((item) => (
          <RecurringYearKpi
            key={item.key}
            {...item}
            data={kpis[item.key]}
            active={
              item.key === "other"
                ? categoryFilter === "otherSummary"
                : categoryFilter === item.key
            }
            onClick={() => {
              const filterKey = item.key === "other" ? "otherSummary" : item.key;
              onCategoryChange(categoryFilter === filterKey ? "all" : filterKey);
            }}
          />
        ))}
      </div>

      <div style={styles.panel}>
        {loading ? (
          <div className="loading" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table className="mb-0" style={{ minWidth: 1540, tableLayout: "fixed" }}>
              <thead style={styles.tableHead}>
                <tr>
                  <th
                    style={{
                      width: 190,
                      position: "sticky",
                      left: 0,
                      zIndex: 4,
                      background: "#fbfdff",
                      verticalAlign: "middle",
                    }}
                  >
                    Danh mục
                  </th>
                  {data.months.map((month) => {
                    const active = month.month === selectedMonth;
                    return (
                      <th
                        key={month.month}
                        className="text-center"
                        style={{
                          width: 112,
                          cursor: "pointer",
                          color: active ? "#0d6efd" : "#334155",
                          background: active ? "#eff6ff" : "#fbfdff",
                          borderTop: active ? "2px solid #0d6efd" : "2px solid transparent",
                        }}
                        onClick={() => onSelectMonth(month.month)}
                      >
                        {String(month.month).padStart(2, "0")}/{data.year}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => {
                  const meta = getCategoryMeta(row.category);
                  return (
                    <tr key={row.key}>
                      <td
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          background: "#fff",
                          width: 190,
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <span
                            className="d-flex align-items-center justify-content-center mr-2 flex-shrink-0"
                            style={{ width: 32, height: 32, borderRadius: "50%", background: meta.bg, color: meta.color }}
                          >
                            <i className={meta.icon} />
                          </span>
                          <div className="min-width-zero">
                            <div className="font-weight-bold text-truncate">{row.label}</div>
                            <div className="text-muted text-small">
                              {row.key === "other" ? "Không thuộc nhóm cố định" : row.key === "salary" ? "Từ UserStaff" : "Theo tháng"}
                            </div>
                          </div>
                        </div>
                      </td>
                      {data.months.map((month) => {
                        const cell = month.categories[row.key];
                        const amount = getRecurringCellAmount(cell, row.key, statusFilter);
                        const status =
                          statusFilter !== "all" && row.key !== "salary"
                            ? (cell.statusCounts?.[statusFilter] ? statusFilter : "empty")
                            : getRecurringCellStatus(cell, row.key === "salary");
                        const statusMeta = RECURRING_STATUS_META[status];
                        const active = month.month === selectedMonth;
                        const statusTitle = `Đã duyệt: ${cell.statusCounts.approved}; Chờ duyệt: ${cell.statusCounts.pending}; Đã từ chối: ${cell.statusCounts.rejected}`;
                        return (
                          <td
                            key={month.month}
                            className="text-center align-middle"
                            role="button"
                            tabIndex="0"
                            title={row.key === "salary" ? "Lương thực tế tổng hợp từ UserStaff" : statusTitle}
                            onClick={() => onSelectMonth(month.month)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") onSelectMonth(month.month);
                            }}
                            style={{
                              cursor: "pointer",
                              background: active ? "#f8fbff" : "#fff",
                              borderLeft: active ? "1px solid #bfdbfe" : undefined,
                              borderRight: active ? "1px solid #bfdbfe" : undefined,
                            }}
                          >
                            <span className="font-weight-bold" style={{ color: amount > 0 ? "#172554" : "#94a3b8", whiteSpace: "nowrap" }}>
                              {amount > 0 ? formatMoney(amount) : "-"}
                            </span>
                            <span
                              className="d-inline-block ml-2"
                              title={statusMeta.label}
                              style={{ width: 7, height: 7, borderRadius: "50%", background: statusMeta.color }}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: "#fbfdff" }}>
                  <th style={{ position: "sticky", left: 0, zIndex: 3, background: "#fbfdff" }}>TỔNG CỘNG</th>
                  {data.months.map((month) => {
                    const total = visibleRows.reduce(
                      (sum, row) => sum + getRecurringCellAmount(month.categories[row.key], row.key, statusFilter),
                      0,
                    );
                    const active = month.month === selectedMonth;
                    return (
                      <th key={month.month} className="text-center" style={{ color: active ? "#0d6efd" : "#172554", background: active ? "#eff6ff" : "#fbfdff" }}>
                        {formatMoney(total)}
                      </th>
                    );
                  })}
                </tr>
              </tfoot>
            </Table>
          </div>
        )}
      </div>

      <div className="d-flex flex-wrap align-items-center mt-3 mb-5 text-small" style={{ gap: 18 }}>
        {Object.entries(RECURRING_STATUS_META)
          .filter(([key]) => ["approved", "pending", "rejected", "mixed", "automatic"].includes(key))
          .map(([key, meta]) => (
            <span key={key} className="d-flex align-items-center text-muted">
              <span className="mr-2" style={{ width: 8, height: 8, borderRadius: "50%", background: meta.color }} />
              {meta.label}
            </span>
          ))}
      </div>
    </>
  );
};

const FilterChip = ({ active, label, count, color = "primary", onClick }) => (
  <Button
    size="sm"
    outline={!active}
    color={active ? color : "light"}
    className="mr-2 mb-2"
    style={{
      minWidth: 76,
      minHeight: 30,
      padding: "3px 8px",
      borderRadius: 6,
      borderColor: active ? undefined : "#94a3b8",
      background: active ? undefined : "#ffffff",
      color: active ? undefined : "#334155",
      fontSize: 12,
      fontWeight: 600,
      lineHeight: "18px",
      opacity: 1,
      boxShadow: active ? "0 3px 8px rgba(37, 99, 235, 0.14)" : "none",
    }}
    onClick={onClick}
  >
    {label}
    <span
      className="ml-2 d-inline-flex align-items-center justify-content-center"
      style={{
        minWidth: 20,
        height: 18,
        padding: "0 5px",
        borderRadius: 9,
        background: active ? "rgba(255, 255, 255, 0.24)" : "#e2e8f0",
        color: active ? "inherit" : "#1e293b",
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {count}
    </span>
  </Button>
);

const CategoryBadge = ({ category }) => {
  const meta = getCategoryMeta(category);

  return (
    <div className="d-flex align-items-center">
      <span
        className="d-flex align-items-center justify-content-center mr-2"
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: meta.bg,
          color: meta.color,
          flex: "0 0 28px",
        }}
      >
        <i className={meta.icon} style={{ fontSize: 15 }} />
      </span>
      <span>{category}</span>
    </div>
  );
};

const InspectorField = ({ label, value, danger }) => (
  <div className="d-flex justify-content-between align-items-start mb-3">
    <span className="text-muted text-small" style={{ flex: "0 0 44%" }}>
      {label}
    </span>
    <span
      className="font-weight-bold text-right"
      style={{
        color: danger ? "#ef4444" : "#172554",
        flex: "1 1 auto",
        lineHeight: "18px",
      }}
    >
      {value || "-"}
    </span>
  </div>
);

const CashFlowPage = ({ searchKeyword, setSearchContext: updateSearchContext }) => {
  const initialDate = new Date();
  const initialRecurringPeriod = `${initialDate.getFullYear()}-${String(
    initialDate.getMonth() + 1,
  ).padStart(2, "0")}`;
  const socketRef = useRef(null);
  const [items, setItems] = useState([]);
  const [dynamicForms, setDynamicForms] = useState({
    [CASH_FLOW_TYPE.INCOME]: [],
    [CASH_FLOW_TYPE.OUTCOME]: [],
  });
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeRecordId, setActiveRecordId] = useState("");
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [statusFilter, setStatusFilter] = useState("all");
  const [statisticFilter, setStatisticFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [show, setShow] = useState(EMPTY_SHOW_STATE);
  const [createPickerOpen, setCreatePickerOpen] = useState(false);
  const [selectedVoucherKey, setSelectedVoucherKey] = useState("");
  const [customVoucherTitle, setCustomVoucherTitle] = useState("");
  const [inspectorTab, setInspectorTab] = useState("info");
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [recurringPeriod, setRecurringPeriod] = useState(initialRecurringPeriod);
  const [recurringYearData, setRecurringYearData] = useState(() =>
    normalizeRecurringYearData(null, initialDate.getFullYear()),
  );
  const [recurringLoading, setRecurringLoading] = useState(false);

  const hasIncomeRead = canReadType(CASH_FLOW_TYPE.INCOME);
  const hasOutcomeRead = canReadType(CASH_FLOW_TYPE.OUTCOME);
  const hasAnyRead = hasIncomeRead || hasOutcomeRead;
  const recurringYear = Number(recurringPeriod.slice(0, 4));

  const emitLog = useCallback((message) => {
    if (!message || !socketRef.current) return;

    socketRef.current.emit("create logs", {
      user_id: localStorage.getItem("user_id"),
      message,
    });
  }, []);

  const fetchCashFlowData = useCallback(async () => {
    const readableTypes = CASH_FLOW_TYPES.filter(canReadType);

    if (!readableTypes.length) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const results = await Promise.allSettled(
      readableTypes.map((type) =>
        axios
          .get(
            `${END_POINT}/${getTypeMeta(type).endpoint}/json?pageSize=10000&currentPage=1&search=`,
            { headers: getAuthHeaders() },
          )
          .then((res) => ({ type, payload: res.data })),
      ),
    );

    const nextItems = [];
    const nextDynamicForms = {
      [CASH_FLOW_TYPE.INCOME]: [],
      [CASH_FLOW_TYPE.OUTCOME]: [],
    };
    let hasError = false;

    results.forEach((result) => {
      if (result.status !== "fulfilled") {
        hasError = true;
        return;
      }

      const { type, payload } = result.value;
      const data = Array.isArray(payload?.data) ? payload.data : [];
      nextDynamicForms[type] = Array.isArray(payload?.dataDynamicForm)
        ? payload.dataDynamicForm
        : [];
      data.forEach((item) => {
        nextItems.push(normalizeCashFlowItem(item, type));
      });
    });

    if (hasError) {
      NotificatioErrController("Không thể tải đầy đủ dữ liệu Thu - Chi");
    }

    setItems(sortCashFlowItems(nextItems));
    setDynamicForms(nextDynamicForms);
    setSelectedItems([]);
    setLoading(false);
  }, []);

  const fetchRecurringYear = useCallback(async (year) => {
    setRecurringLoading(true);
    try {
      const response = await axios.get(`${END_POINT}/outcome/recurring-year?year=${year}`, {
        headers: getAuthHeaders(),
      });
      setRecurringYearData(normalizeRecurringYearData(response.data?.data, year));
    } catch (error) {
      setRecurringYearData(normalizeRecurringYearData(null, year));
      NotificatioErrController(
        error.response?.data?.responseText || "Không thể tải dữ liệu định kỳ",
      );
    } finally {
      setRecurringLoading(false);
    }
  }, []);

  useEffect(() => {
    updateSearchContext("cashFlow");
    fetchCashFlowData();
  }, [fetchCashFlowData, updateSearchContext]);

  useEffect(() => {
    if (activeTab !== "recurring" || !hasOutcomeRead) return;
    fetchRecurringYear(recurringYear);
  }, [activeTab, fetchRecurringYear, hasOutcomeRead, recurringYear]);

  useEffect(() => {
    socketRef.current = createSocket();
    socketRef.current.emit("join room", {
      room_id: localStorage.getItem("user_id"),
    });

    return () => {
      if (socketRef.current?.disconnect) socketRef.current.disconnect();
      else if (socketRef.current?.close) socketRef.current.close();
    };
  }, []);

  useEffect(() => {
    setSearch(searchKeyword || "");
  }, [searchKeyword]);

  const tabRecords = useMemo(() => getTabRecords(items, activeTab), [activeTab, items]);

  const categoryOptions = useMemo(() => {
    const unique = new Set(["Marketing", "Điện", "Nước", "Chi khác"]);
    tabRecords.forEach((item) => {
      if (item.category) unique.add(item.category);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, "vi"));
  }, [tabRecords]);

  const baseFilteredItems = useMemo(() => {
    let records = filterCashFlowItems(tabRecords, {
      type: "all",
      status: statusFilter,
      statistic: statisticFilter,
      search,
    });

    if (categoryFilter !== "all") {
      records = records.filter((item) => item.category === categoryFilter);
    }

    if (periodFilter) {
      records = records.filter((item) => matchesCashFlowPeriod(item, periodFilter));
    }

    return sortCashFlowItems(records);
  }, [categoryFilter, periodFilter, search, statisticFilter, statusFilter, tabRecords]);

  const totalPage = Math.max(1, Math.ceil(baseFilteredItems.length / pageSize));
  const pagedItems = useMemo(() => {
    const safePage = Math.min(currentPage, totalPage);
    const start = (safePage - 1) * pageSize;
    return baseFilteredItems.slice(start, start + pageSize);
  }, [baseFilteredItems, currentPage, pageSize, totalPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, categoryFilter, periodFilter, search, statisticFilter]);

  useEffect(() => {
    if (currentPage > totalPage) setCurrentPage(totalPage);
  }, [currentPage, totalPage]);

  useEffect(() => {
    if (!baseFilteredItems.length) {
      setActiveRecordId("");
      return;
    }

    if (!baseFilteredItems.some((item) => item.cashFlowId === activeRecordId)) {
      setActiveRecordId(baseFilteredItems[0].cashFlowId);
    }
  }, [activeRecordId, baseFilteredItems]);

  const summary = useMemo(() => buildCashFlowSummary(tabRecords), [tabRecords]);
  const currentSummary = useMemo(
    () => buildCashFlowSummary(baseFilteredItems),
    [baseFilteredItems],
  );

  const selectedRecords = useMemo(
    () => items.filter((item) => selectedItems.includes(item.cashFlowId)),
    [items, selectedItems],
  );

  const activeRecord = useMemo(
    () =>
      baseFilteredItems.find((item) => item.cashFlowId === activeRecordId) ||
      baseFilteredItems[0] ||
      null,
    [activeRecordId, baseFilteredItems],
  );

  const selectedCategoryStats = useMemo(() => {
    if (!activeRecord) return null;

    const sameCategory = items.filter(
      (item) => item.type === activeRecord.type && item.category === activeRecord.category,
    );
    const stats = buildCashFlowSummary(sameCategory);
    const approvedTotal =
      activeRecord.type === CASH_FLOW_TYPE.OUTCOME ? stats.totalOutcome : stats.totalIncome;
    const grossTotal =
      activeRecord.type === CASH_FLOW_TYPE.OUTCOME ? stats.grossOutcome : stats.grossIncome;

    return {
      count: sameCategory.length,
      approvedTotal,
      grossTotal,
      monthTotal: sameCategory
        .filter((item) => item.period === activeRecord.period)
        .reduce((sum, item) => sum + item.amount, 0),
    };
  }, [activeRecord, items]);

  const pageIds = pagedItems.map((item) => item.cashFlowId);
  const isPageChecked =
    pageIds.length > 0 && pageIds.every((id) => selectedItems.includes(id));

  const modalSetting = useMemo(
    () => ({
      add: show.addIncome || show.addOutcome,
      update: show.updateIncome || show.updateOutcome,
      viewonly: false,
    }),
    [show],
  );

  const selectedCreateOption = useMemo(
    () => CREATE_VOUCHER_OPTIONS.find((option) => option.key === selectedVoucherKey) || null,
    [selectedVoucherKey],
  );

  const closeModals = () => {
    setShow(EMPTY_SHOW_STATE);
    setForm(DEFAULT_FORM);
  };

  const openAdd = (type, initialValues = {}) => {
    if (!canCreateType(type)) return;

    setForm({
      ...DEFAULT_FORM,
      ...initialValues,
      type,
    });
    setShow({
      ...EMPTY_SHOW_STATE,
      addIncome: type === CASH_FLOW_TYPE.INCOME,
      addOutcome: type === CASH_FLOW_TYPE.OUTCOME,
    });
  };

  const openCreatePicker = () => {
    setSelectedVoucherKey("");
    setCustomVoucherTitle("");
    setCreatePickerOpen(true);
  };

  const closeCreatePicker = () => {
    setCreatePickerOpen(false);
    setSelectedVoucherKey("");
    setCustomVoucherTitle("");
  };

  const createSelectedVoucher = () => {
    if (!selectedCreateOption) {
      NotificatioErrController("Vui lòng chọn loại phiếu cần tạo");
      return;
    }

    if (!canCreateType(selectedCreateOption.type)) return;

    const customTitle = customVoucherTitle.trim();
    if (selectedCreateOption.requiresTitle && !customTitle) {
      NotificatioErrController("Vui lòng nhập tiêu đề cho phiếu khác");
      return;
    }

    const initialTitle = selectedCreateOption.requiresTitle
      ? customTitle
      : selectedCreateOption.title || "";
    const formConfig = getCashFlowCreateFormConfig(
      selectedCreateOption.key,
      selectedCreateOption.type,
    );

    closeCreatePicker();
    openAdd(selectedCreateOption.type, {
      sub_title: initialTitle,
      time: formConfig.defaultTime || "",
      voucherTemplate: selectedCreateOption.key,
    });
  };

  const openUpdate = (item) => {
    if (!canUpdateType(item.type)) return;

    setForm({
      ...DEFAULT_FORM,
      ...item,
      dynamic_form: item.dynamic_form || {},
      type: item.type,
      user: getCreatorName(item),
    });
    setShow({
      ...EMPTY_SHOW_STATE,
      updateIncome: item.type === CASH_FLOW_TYPE.INCOME,
      updateOutcome: item.type === CASH_FLOW_TYPE.OUTCOME,
    });
  };

  const openRefund = (item) => {
    if (!item) return;

    const refundType =
      item.type === CASH_FLOW_TYPE.OUTCOME ? CASH_FLOW_TYPE.INCOME : CASH_FLOW_TYPE.OUTCOME;

    openAdd(refundType, {
      sub_title: `Hoàn tiền ${item.sub_title || item.name || ""}`.trim(),
      name: item.name || "",
      value: String(item.amount || 0),
      note: `Hoàn tiền từ ${item.voucherCode}`,
      time: "now",
      address: item.address || "",
    });
  };

  const onChangeForm = (event, name) => {
    if (event?.target) {
      const { name: fieldName, value } = event.target;
      setForm((prev) => ({
        ...prev,
        [fieldName]: fieldName === "value" ? normalizeCashInputValue(value) : value,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: event?.value || "",
    }));
  };

  const onChangeDynamicForm = (event, name) => {
    if (event?.target) {
      const { name: fieldName, value } = event.target;
      setForm((prev) => ({
        ...prev,
        dynamic_form: {
          ...prev.dynamic_form,
          [fieldName]: value,
        },
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      dynamic_form: {
        ...prev.dynamic_form,
        [name]: event?.value || event,
      },
    }));
  };

  const validateForm = (type) => {
    if (!form.sub_title || !form.name || !form.time) {
      NotificatioErrController(
        type === CASH_FLOW_TYPE.INCOME
          ? "Vui lòng nhập đủ tiêu đề, người nộp và trạng thái thống kê"
          : "Vui lòng nhập đủ tiêu đề, người nhận và trạng thái thống kê",
      );
      return false;
    }

    if (!cleanCashValue(form.value)) {
      NotificatioErrController("Số tiền không hợp lệ");
      return false;
    }

    return true;
  };

  const submitCreate = async (event, type) => {
    event.preventDefault();
    if (!canCreateType(type) || !validateForm(type)) return;

    const meta = getTypeMeta(type);

    try {
      await axios.post(
        `${END_POINT}/${meta.endpoint}`,
        {
          value: cleanCashValue(form.value),
          sub_title: form.sub_title,
          name: form.name,
          note: form.note,
          time: form.time,
          address: form.address,
          dynamic_form: form.dynamic_form,
        },
        { headers: getAuthHeaders() },
      );

      NotificatioSucController("Thành công");
      emitLog(LogsHelper[meta.logCreate]);
      closeModals();
      fetchCashFlowData();
    } catch (err) {
      NotificatioErrController(err.response?.data?.responseText);
    }
  };

  const submitUpdate = async (event, type) => {
    event.preventDefault();
    if (!canUpdateType(type) || !form.id || !validateForm(type)) return;

    const meta = getTypeMeta(type);

    try {
      await axios.put(
        `${END_POINT}/${meta.endpoint}`,
        {
          id: form.id,
          value: cleanCashValue(form.value),
          sub_title: form.sub_title,
          name: form.name,
          note: form.note,
          time: form.time,
          address: form.address,
          dynamic_form: form.dynamic_form,
        },
        { headers: getAuthHeaders() },
      );

      NotificatioSucController("Thành công");
      emitLog(LogsHelper[meta.logUpdate]);
      closeModals();
      fetchCashFlowData();
    } catch (err) {
      NotificatioErrController(err.response?.data?.responseText);
    }
  };

  const deleteRecords = async (records) => {
    const deletableRecords = records.filter((item) => canDeleteType(item.type));

    if (!deletableRecords.length) return;
    if (!window.confirm(`Bạn chắc chắn muốn xoá ${deletableRecords.length} phiếu đã chọn?`)) {
      return;
    }

    const groupedIds = deletableRecords.reduce((acc, item) => {
      acc[item.type] = acc[item.type] || [];
      acc[item.type].push(item.id);
      return acc;
    }, {});

    try {
      await Promise.all(
        Object.keys(groupedIds).map((type) =>
          axios.delete(`${END_POINT}/${getTypeMeta(type).endpoint}`, {
            data: { arrayId: groupedIds[type] },
            headers: getAuthHeaders(),
          }),
        ),
      );

      NotificatioSucController("Thành công");
      Object.keys(groupedIds).forEach((type) => {
        emitLog(LogsHelper[getTypeMeta(type).logDelete]);
      });
      setSelectedItems([]);
      fetchCashFlowData();
    } catch (err) {
      NotificatioErrController(err.response?.data?.responseText);
    }
  };

  const moveRecurringPeriod = (amount) => {
    const [year, month] = recurringPeriod.split("-").map(Number);
    const nextDate = new Date(year, month - 1 + amount, 1);
    setRecurringPeriod(
      `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, "0")}`,
    );
    setSelectedItems([]);
  };

  const selectRecurringMonth = (month) => {
    setRecurringPeriod(`${recurringYearData.year}-${String(month).padStart(2, "0")}`);
  };

  const updateStatus = async (item, status) => {
    if (!canUpdateType(item.type)) return;

    const actionLabel = status === "approve" ? "Xét duyệt" : "Từ chối";
    if (
      !window.confirm(
        `Bạn chắc chắn muốn "${actionLabel}" ${getTypeMeta(item.type).fullLabel} này?`,
      )
    ) {
      return;
    }

    const meta = getTypeMeta(item.type);

    try {
      await axios.put(
        `${END_POINT}/${meta.endpoint}/update_status`,
        {
          id: item.id,
          value: item.value,
          status,
        },
        { headers: getAuthHeaders() },
      );

      NotificatioSucController("Thành công");
      emitLog(LogsHelper[meta.logUpdate]);
      fetchCashFlowData();
    } catch (err) {
      NotificatioErrController(err.response?.data?.responseText);
    }
  };

  const toggleSelected = (item) => {
    setSelectedItems((prev) =>
      prev.includes(item.cashFlowId)
        ? prev.filter((id) => id !== item.cashFlowId)
        : [...prev, item.cashFlowId],
    );
  };

  const toggleCurrentPage = () => {
    setSelectedItems((prev) => {
      if (isPageChecked) return prev.filter((id) => !pageIds.includes(id));
      return Array.from(new Set([...prev, ...pageIds]));
    });
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setStatisticFilter("all");
    setCategoryFilter("all");
    setPeriodFilter("");
    setSearch("");
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedItems([]);
    setCategoryFilter("all");
    setStatusFilter("all");
    setStatisticFilter("all");
  };

  const currentUserId = Number(localStorage.getItem("user_id"));
  const activeMeta = activeRecord ? getTypeMeta(activeRecord.type) : getTypeMeta(CASH_FLOW_TYPE.OUTCOME);
  const isIncomeView = activeTab === CASH_FLOW_TYPE.INCOME;
  const isOutcomeView = activeTab === CASH_FLOW_TYPE.OUTCOME;
  const isRecurringView = activeTab === "recurring";
  const dateColumnLabel = isIncomeView ? "Ngày thu" : isOutcomeView || isRecurringView ? "Ngày chi" : "Ngày ghi";
  const approvedChipLabel = isIncomeView ? "Đã thu" : isOutcomeView || isRecurringView ? "Đã chi" : "Đã duyệt";
  const actorFieldLabel =
    activeRecord?.type === CASH_FLOW_TYPE.INCOME ? "Người nộp" : "Người nhận / Nhà cung cấp";
  const fourthKpi =
    activeTab === "overview"
      ? {
          label: "Dòng tiền ròng",
          value: summary.netCashFlow,
          hint: `${currentSummary.totalCount} phiếu đang hiển thị`,
        }
      : {
          label: "Chưa vào thống kê",
          value: Math.max(
            0,
            activeTab === CASH_FLOW_TYPE.INCOME
              ? summary.grossIncome - summary.totalIncome
              : summary.grossOutcome - summary.totalOutcome,
          ),
          hint: `${summary.excludedCount} phiếu ngoài thống kê`,
        };
  const canApproveActive =
    activeRecord &&
    activeRecord.status === CASH_FLOW_STATUS.PENDING &&
    currentUserId !== Number(activeRecord.user_created_id) &&
    canUpdateType(activeRecord.type);

  if (!hasAnyRead) {
    return (
      <Card className="text-muted mh-40vh h-100">
        <CardBody className="d-flex justify-content-center align-items-center">
          Bạn không có quyền sử dụng tính năng Thu - Chi.
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="disable-text-selection" style={styles.page}>
      {/* <div style={styles.header}>
        <div className="d-flex align-items-center flex-wrap">
          <h1 className="mb-0 mr-3" style={{ fontSize: 28, lineHeight: "34px" }}>
            Thu - Chi
          </h1>
          <span className="text-muted">Quản lý các khoản thu và chi của cửa hàng</span>
        </div>
        <Button color="link" className="p-0 font-weight-bold">
          <i className="simple-icon-question mr-1" />
          Hướng dẫn
        </Button>
      </div> */}

      <div style={styles.tabs} className="mb-3">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            style={{
              ...styles.tab,
              ...(activeTab === tab.key ? styles.activeTab : {}),
            }}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
        <Button
          color="primary"
          className="ml-auto d-flex align-items-center flex-shrink-0"
          style={{ borderRadius: 8, minHeight: 38, whiteSpace: "nowrap" }}
          onClick={openCreatePicker}
          disabled={!canCreateType(CASH_FLOW_TYPE.INCOME) && !canCreateType(CASH_FLOW_TYPE.OUTCOME)}
        >
          <i className="simple-icon-plus mr-2" />
          Thêm phiếu
        </Button>
      </div>

      <div style={styles.content} className="mb-5">
        <div style={styles.ledger}>
          {isRecurringView ? (
            <RecurringYearView
              data={recurringYearData}
              loading={recurringLoading}
              selectedPeriod={recurringPeriod}
              categoryFilter={categoryFilter}
              statusFilter={statusFilter}
              onPeriodChange={(value) => {
                setRecurringPeriod(value);
                setSelectedItems([]);
              }}
              onMovePeriod={moveRecurringPeriod}
              onCategoryChange={(value) => {
                setCategoryFilter(value);
                setSelectedItems([]);
              }}
              onStatusChange={(value) => {
                setStatusFilter(value);
                setSelectedItems([]);
              }}
              onSelectMonth={selectRecurringMonth}
            />
          ) : (
            <>
          {isIncomeView || isOutcomeView ? null : (
            <Row>
              <Col xl="3" lg="6" sm="6" xs="12">
                <CashFlowKpi
                  label={isIncomeView ? "Tổng thu" : isOutcomeView ? "Tổng chi" : "Tổng thu hiệu lực"}
                  value={formatMoney(
                    isOutcomeView
                      ? summary.grossOutcome
                      : isIncomeView
                        ? summary.grossIncome
                        : summary.totalIncome,
                  )}
                  hint={`${summary.totalCount} phiếu trong phạm vi`}
                  color={{ text: "#047857", bg: "#dcfce7" }}
                  icon={isIncomeView ? "iconsminds-add-file" : "iconsminds-download"}
                />
              </Col>
              <Col xl="3" lg="6" sm="6" xs="12">
                <CashFlowKpi
                  label={isIncomeView ? "Đã thu" : isOutcomeView ? "Đã chi" : "Chi hiệu lực"}
                  value={formatMoney(isIncomeView ? summary.totalIncome : summary.totalOutcome)}
                  hint={
                    isIncomeView
                      ? `${summary.incomeCount} phiếu thu`
                      : `${summary.outcomeCount} phiếu chi`
                  }
                  color={{ text: "#ef4444", bg: "#fee2e2" }}
                  icon="iconsminds-wallet"
                />
              </Col>
              <Col xl="3" lg="6" sm="6" xs="12">
                <CashFlowKpi
                  label="Chờ duyệt"
                  value={formatMoney(summary.pendingAmount)}
                  hint={`${summary.pendingCount} phiếu cần xử lý`}
                  color={{ text: "#f97316", bg: "#ffedd5" }}
                  icon="iconsminds-clock"
                />
              </Col>
              <Col xl="3" lg="6" sm="6" xs="12">
                <CashFlowKpi
                  label={fourthKpi.label}
                  value={formatMoney(fourthKpi.value)}
                  hint={fourthKpi.hint}
                  color={{ text: "#0d6efd", bg: "#eff6ff" }}
                  icon="iconsminds-pie-chart"
                />
              </Col>
            </Row>
          )}

          <div style={styles.panel} className="mb-3">
            <div className="p-3">
              <Row className="align-items-center">
                {/* <Col xl="4" lg="12" className="mb-3 mb-xl-0">
                  <Input
                    style={styles.filterInput}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Tìm phiếu, nội dung, người nhận..."
                  />
                </Col> */}
                <Col xl="2" md="4" sm="6" className="mb-3 mb-xl-0">
                  <Input
                    type="select"
                    style={styles.filterInput}
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="all">Tất cả danh mục</option>
                    {categoryOptions.map((category) => (
                      <option key={category} value={category}>
                        {category === "Chi khác" ? "Khác" : category}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col xl="3" md="8" sm="12" className="mb-3 mb-xl-0">
                  <PeriodFilter value={periodFilter} onChange={setPeriodFilter} />
                </Col>
                <Col xl="2" md="12" className="d-flex mb-3 mb-xl-0">
                  {categoryFilter !== "all" || periodFilter ? (
                    <Button
                      size="sm"
                      style={styles.softButton}
                      className="mr-2"
                      onClick={clearFilters}
                    >
                      <i className="iconsminds-filter-2 mr-1" />
                      Đặt lại
                    </Button>
                  ) : null}
                  {/* <Button size="sm" style={styles.softButton} onClick={fetchCashFlowData}>
                    <i className="simple-icon-refresh" />
                  </Button> */}
                </Col>
                <Col xl="5" md="12" className="d-flex justify-content-end mb-3 mb-xl-0">
                  {selectedRecords.length > 0 ? (
                    <Button
                      color="danger"
                      outline
                      size="sm"
                      style={styles.compactButton}
                      onClick={() => deleteRecords(selectedRecords)}
                    >
                      <i className="simple-icon-trash mr-2" />
                      Xóa ({selectedRecords.length})
                    </Button>
                  ) : null}
                </Col>
              </Row>

              <div className="d-flex flex-wrap align-items-center mt-3">
                <div>
                  <FilterChip
                    active={statusFilter === "all" && statisticFilter === "all"}
                    label="Tất cả"
                    count={tabRecords.length}
                    onClick={() => {
                      setStatusFilter("all");
                      setStatisticFilter("all");
                    }}
                  />
                  <FilterChip
                    active={statusFilter === CASH_FLOW_STATUS.APPROVED}
                    label={approvedChipLabel}
                    count={tabRecords.filter((item) => item.status === CASH_FLOW_STATUS.APPROVED).length}
                    color="success"
                    onClick={() => setStatusFilter(CASH_FLOW_STATUS.APPROVED)}
                  />
                  <FilterChip
                    active={statusFilter === CASH_FLOW_STATUS.PENDING}
                    label="Chờ duyệt"
                    count={tabRecords.filter((item) => item.status === CASH_FLOW_STATUS.PENDING).length}
                    color="warning"
                    onClick={() => setStatusFilter(CASH_FLOW_STATUS.PENDING)}
                  />
                  <FilterChip
                    active={statisticFilter === "included"}
                    label="Định kỳ"
                    count={tabRecords.filter((item) => item.isIncludedInStats).length}
                    color="secondary"
                    onClick={() => setStatisticFilter("included")}
                  />
                  <FilterChip
                    active={statusFilter === CASH_FLOW_STATUS.REJECTED}
                    label="Đã từ chối"
                    count={tabRecords.filter((item) => item.status === CASH_FLOW_STATUS.REJECTED).length}
                    color="danger"
                    onClick={() => setStatusFilter(CASH_FLOW_STATUS.REJECTED)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={styles.panel}>
            <div className="p-0">
              {loading ? (
                <div className="loading" />
              ) : baseFilteredItems.length === 0 ? (
                <div className="text-center text-muted py-5">Chưa có dữ liệu phù hợp.</div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead style={styles.tableHead}>
                    <tr>
                      <th style={{ width: 42 }}>
                        <CustomInput
                          type="checkbox"
                          id="cash-flow-select-page"
                          label=""
                          checked={isPageChecked}
                          onChange={toggleCurrentPage}
                        />
                      </th>
                      <th style={{ width: 130 }}>Mã phiếu</th>
                      <th style={{ width: 120 }}>{dateColumnLabel}</th>
                      <th style={{ width: 130 }}>Danh mục</th>
                      <th>Nội dung</th>
                      <th style={{ width: 95 }}>Kỳ / Tháng</th>
                      <th className="text-right" style={{ width: 130 }}>
                        Số tiền
                      </th>
                      <th style={{ width: 120 }}>Trạng thái</th>
                      <th style={{ width: 120 }}>Người lập</th>
                      <th className="text-right" style={{ width: 46 }}>
                        <i className="simple-icon-settings" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedItems.map((item) => {
                      const meta = getTypeMeta(item.type);
                      const selected = activeRecord?.cashFlowId === item.cashFlowId;

                      return (
                        <tr
                          key={item.cashFlowId}
                          style={{
                            ...styles.tableRow,
                            ...(selected ? styles.selectedRow : {}),
                          }}
                          onClick={() => {
                            setActiveRecordId(item.cashFlowId);
                            setInspectorOpen(true);
                          }}
                        >
                          <td className="align-middle" onClick={(e) => e.stopPropagation()}>
                            <CustomInput
                              type="checkbox"
                              id={`cash-flow-${item.cashFlowId}`}
                              label=""
                              checked={selectedItems.includes(item.cashFlowId)}
                              onChange={() => toggleSelected(item)}
                            />
                          </td>
                          <td className="align-middle font-weight-bold text-primary">
                            {item.voucherCode}
                          </td>
                          <td className="align-middle">
                            <div>{formatDateOnly(item)}</div>
                            <div className="text-muted text-small">
                              {formatDateTime(item).split(" ")[1] || ""}
                            </div>
                          </td>
                          <td className="align-middle">
                            <CategoryBadge category={item.category} />
                          </td>
                          <td className="align-middle">
                            <div className="font-weight-bold">{item.sub_title}</div>
                            <div className="text-muted text-small">{item.note || item.address || ""}</div>
                          </td>
                          <td className="align-middle">{item.period || getVisiblePeriodLabel(periodFilter)}</td>
                          <td
                            className="align-middle text-right font-weight-bold"
                            style={{ color: meta.tone, whiteSpace: "nowrap" }}
                          >
                            {item.type === CASH_FLOW_TYPE.OUTCOME ? "-" : "+"}
                            {formatMoney(item.amount)}
                          </td>
                          <td className="align-middle">
                            <Badge color={getStatusColor(item.status)} pill>
                              {getStatusLabel(item.status)}
                            </Badge>
                          </td>
                          <td className="align-middle">{getCreatorName(item)}</td>
                          {/* <td className="align-middle text-right" onClick={(e) => e.stopPropagation()}>
                            <Button
                              color="link"
                              size="sm"
                              className="p-0"
                              onClick={() => openUpdate(item)}
                              disabled={!canUpdateType(item.type)}
                            >
                              <i className="simple-icon-options-vertical" />
                            </Button>
                          </td> */}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>

            <div className="d-flex flex-wrap align-items-center justify-content-between p-3 border-top">
              <div className="d-flex align-items-center mb-2 mb-md-0">
                <Button
                  color="light"
                  className="mr-2"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                >
                  <i className="simple-icon-arrow-left" />
                </Button>
                {Array.from({ length: Math.min(totalPage, 4) }).map((_, index) => {
                  const page = index + 1;
                  return (
                    <Button
                      key={page}
                      color={currentPage === page ? "primary" : "light"}
                      className="mr-2"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Button>
                  );
                })}
                <Button
                  color="light"
                  disabled={currentPage >= totalPage}
                  onClick={() => setCurrentPage((prev) => Math.min(totalPage, prev + 1))}
                >
                  <i className="simple-icon-arrow-right" />
                </Button>
              </div>
              <div className="text-muted text-small mb-2 mb-md-0">
                Hiển thị {pagedItems.length ? (currentPage - 1) * pageSize + 1 : 0} -{" "}
                {Math.min(currentPage * pageSize, baseFilteredItems.length)} trên tổng{" "}
                {baseFilteredItems.length} phiếu
              </div>
              <Input
                type="select"
                style={{ width: 130, borderRadius: 8 }}
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <option value={10}>10 / trang</option>
                <option value={20}>20 / trang</option>
                <option value={50}>50 / trang</option>
              </Input>
            </div>
          </div>
            </>
          )}
        </div>

        {!isRecurringView && inspectorOpen && activeRecord ? (
          <aside style={styles.inspector}>
            <div style={styles.panel}>
              <div className="p-4 border-bottom">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <div className="d-flex align-items-center">
                      <h4 className="mb-0 mr-2 font-weight-bold">{activeRecord.voucherCode}</h4>
                      <Badge color={getStatusColor(activeRecord.status)} pill>
                        {getStatusLabel(activeRecord.status)}
                      </Badge>
                    </div>
                    <div className="text-muted text-small mt-1">{activeMeta.fullLabel}</div>
                  </div>
                  <Button color="link" className="p-0 text-muted" onClick={() => setInspectorOpen(false)}>
                    <i className="simple-icon-close" />
                  </Button>
                </div>

                <div className="d-flex flex-nowrap align-items-center mt-3">
                  {false && (
                    <Button color="light" size="sm" className="mr-1 mb-0 text-nowrap" onClick={() => window.print()}>
                      <i className="simple-icon-printer mr-1" />
                      In
                    </Button>
                  )}
                  <Button
                    color="light"
                    size="sm"
                    className="mr-1 mb-0 text-nowrap"
                    onClick={() => openUpdate(activeRecord)}
                    disabled={!canUpdateType(activeRecord.type)}
                  >
                    <i className="simple-icon-pencil mr-1" />
                    Sửa
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    className="mr-1 mb-0 text-nowrap"
                    onClick={() => openRefund(activeRecord)}
                    disabled={
                      !canCreateType(
                        activeRecord.type === CASH_FLOW_TYPE.OUTCOME
                          ? CASH_FLOW_TYPE.INCOME
                          : CASH_FLOW_TYPE.OUTCOME,
                      )
                    }
                  >
                    <i className="iconsminds-refresh mr-1" />
                    Hoàn tiền
                  </Button>
                  <Button
                    color="danger"
                    size="sm"
                    className="mb-0 text-nowrap"
                    onClick={() => deleteRecords([activeRecord])}
                    disabled={!canDeleteType(activeRecord.type)}
                  >
                    <i className="simple-icon-trash mr-1" />
                    Xóa
                  </Button>
                </div>
              </div>

              <div className="d-flex px-4 pt-3 border-bottom">
                {[
                  ["info", "Thông tin"],
                  ["detail", "Chi tiết"],
                  ["approve", "Duyệt"],
                  ["history", "Lịch sử"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    style={{
                      ...styles.tab,
                      marginRight: 28,
                      ...(inspectorTab === key ? styles.activeTab : {}),
                    }}
                    onClick={() => setInspectorTab(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="p-3">
                {inspectorTab === "info" ? (
                  <div style={styles.section}>
                    <h6 className="font-weight-bold mb-4">{`Thông tin ${activeMeta.fullLabel.toLowerCase()}`}</h6>
                    <InspectorField label="Mã phiếu" value={activeRecord.voucherCode} />
                    <InspectorField label="Ngày ghi nhận" value={formatDateTime(activeRecord)} />
                    <InspectorField label={actorFieldLabel} value={activeRecord.name} />
                    <InspectorField label="Địa chỉ / SĐT" value={activeRecord.address} />
                    <InspectorField
                      label="Danh mục"
                      value={
                        <span
                          style={{
                            color: getCategoryMeta(activeRecord.category).color,
                          }}
                        >
                          {activeRecord.category}
                        </span>
                      }
                    />
                    <InspectorField label="Kỳ / Tháng" value={activeRecord.period} />
                    <InspectorField label="Nội dung" value={activeRecord.sub_title} />
                    <InspectorField label="Số tiền" value={formatMoney(activeRecord.amount)} danger />
                    <InspectorField
                      label="Hình thức"
                      value={activeRecord.isIncludedInStats ? "Đưa vào thống kê" : "Không thống kê"}
                    />
                    <InspectorField label="Người lập" value={getCreatorName(activeRecord)} />
                    <InspectorField label="Ghi chú" value={activeRecord.note} />
                  </div>
                ) : null}

                {inspectorTab === "detail" ? (
                  <div style={styles.section}>
                    <h6 className="font-weight-bold mb-4">Chi tiết hạch toán</h6>
                    <InspectorField label="Loại phiếu" value={activeMeta.fullLabel} />
                    <InspectorField label="Trạng thái" value={getStatusLabel(activeRecord.status)} />
                    <InspectorField label="Giá trị gốc" value={formatMoney(activeRecord.amount)} />
                    <InspectorField
                      label="Giá trị dòng tiền"
                      value={`${activeRecord.type === CASH_FLOW_TYPE.OUTCOME ? "-" : "+"}${formatMoney(
                        activeRecord.amount,
                      )}`}
                      danger={activeRecord.type === CASH_FLOW_TYPE.OUTCOME}
                    />
                    <InspectorField label="Ngày cập nhật" value={formatDateTime({ ...activeRecord, createdAt: activeRecord.updatedAt || activeRecord.createdAt })} />
                  </div>
                ) : null}

                {inspectorTab === "approve" ? (
                  <div style={styles.section}>
                    <h6 className="font-weight-bold mb-4">Duyệt phiếu</h6>
                    <InspectorField label="Trạng thái hiện tại" value={getStatusLabel(activeRecord.status)} />
                    <InspectorField label="Người lập" value={getCreatorName(activeRecord)} />
                    <div className="d-flex mt-4">
                      <Button
                        color="success"
                        className="mr-2"
                        disabled={!canApproveActive}
                        onClick={() => updateStatus(activeRecord, "approve")}
                      >
                        Duyệt
                      </Button>
                      <Button
                        color="danger"
                        outline
                        disabled={!canApproveActive}
                        onClick={() => updateStatus(activeRecord, "reject")}
                      >
                        Từ chối
                      </Button>
                    </div>
                  </div>
                ) : null}

                {inspectorTab === "history" ? (
                  <div style={styles.section}>
                    <h6 className="font-weight-bold mb-4">Lịch sử</h6>
                    <InspectorField label="Tạo lúc" value={formatDateTime(activeRecord)} />
                    <InspectorField
                      label="Cập nhật lúc"
                      value={formatDateTime({ ...activeRecord, createdAt: activeRecord.updatedAt || activeRecord.createdAt })}
                    />
                    <InspectorField label="Người lập" value={getCreatorName(activeRecord)} />
                  </div>
                ) : null}

                {/* <div style={styles.section} className="mt-3">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="font-weight-bold mb-0">Đính kèm chứng từ</h6>
                    <Button color="link" className="p-0">
                      <i className="simple-icon-cloud-download" />
                    </Button>
                  </div>
                  <div className="d-flex align-items-center">
                    <div
                      className="d-flex align-items-center justify-content-center mr-3"
                      style={{
                        width: 58,
                        height: 58,
                        borderRadius: 8,
                        background: "#f8fafc",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <i className="iconsminds-file" style={{ fontSize: 24, color: "#64748b" }} />
                    </div>
                    <div>
                      <div className="font-weight-bold">{activeRecord.voucherCode}.pdf</div>
                      <div className="text-muted text-small">Chứng từ nội bộ</div>
                    </div>
                  </div>
                </div> */}

                {selectedCategoryStats ? (
                  <div style={styles.section} className="mt-3">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h6 className="font-weight-bold mb-0">
                        {activeRecord.category}
                      </h6>
                      {/* <Button color="link" className="p-0">
                        Xem báo cáo
                      </Button> */}
                    </div>
                    <Row>
                      <Col xs="6" className="mb-3">
                        <div className="text-muted text-small">Tổng hiệu lực</div>
                        <div className="font-weight-bold">{formatMoney(selectedCategoryStats.approvedTotal)}</div>
                      </Col>
                      <Col xs="6" className="mb-3">
                        <div className="text-muted text-small">Tổng ghi nhận</div>
                        <div className="font-weight-bold">{formatMoney(selectedCategoryStats.grossTotal)}</div>
                      </Col>
                      <Col xs="6">
                        <div className="text-muted text-small">Kỳ này</div>
                        <div className="font-weight-bold">{formatMoney(selectedCategoryStats.monthTotal)}</div>
                      </Col>
                      <Col xs="6">
                        <div className="text-muted text-small">Số phiếu</div>
                        <div className="font-weight-bold">{selectedCategoryStats.count}</div>
                      </Col>
                    </Row>
                  </div>
                ) : null}
              </div>
            </div>
          </aside>
        ) : null}
      </div>

      {/* <Button
        color="primary"
        className="position-fixed d-none d-md-flex align-items-center justify-content-center"
        style={{
          right: 24,
          bottom: 24,
          width: 56,
          height: 56,
          borderRadius: "50%",
          boxShadow: "0 12px 24px rgba(37, 99, 235, 0.32)",
          zIndex: 20,
        }}
        onClick={() => setInspectorOpen(true)}
      >
        <i className="iconsminds-speach-bubble-3" style={{ fontSize: 22 }} />
      </Button> */}

      <Modal isOpen={createPickerOpen} toggle={closeCreatePicker} size="lg">
        <ModalHeader toggle={closeCreatePicker}>Thêm phiếu mới</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <div className="font-weight-bold mb-1">Chọn loại phiếu</div>
            <div className="text-muted text-small">
              Thông tin phù hợp sẽ được điền sẵn vào form tạo phiếu hiện tại.
            </div>
          </div>
          <Row>
            {CREATE_VOUCHER_OPTIONS.map((option) => {
              const selected = selectedVoucherKey === option.key;
              const disabled = !canCreateType(option.type);

              return (
                <Col key={option.key} md="6" className="mb-3">
                  <button
                    type="button"
                    className="w-100 d-flex align-items-center text-left"
                    aria-pressed={selected}
                    disabled={disabled}
                    style={{
                      minHeight: 72,
                      padding: "12px 14px",
                      borderRadius: 8,
                      border: selected ? "1px solid #2563eb" : "1px solid #e2e8f0",
                      background: selected ? "#eff6ff" : "#ffffff",
                      boxShadow: selected ? "0 0 0 2px rgba(37, 99, 235, 0.12)" : "none",
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.5 : 1,
                      outline: "none",
                    }}
                    onClick={() => {
                      setSelectedVoucherKey(option.key);
                      if (!option.requiresTitle) setCustomVoucherTitle("");
                    }}
                  >
                    <span
                      className="d-flex align-items-center justify-content-center mr-3"
                      style={{
                        width: 40,
                        height: 40,
                        flex: "0 0 40px",
                        borderRadius: "50%",
                        color: option.color,
                        background: option.bg,
                      }}
                    >
                      <i className={option.icon} style={{ fontSize: 19 }} />
                    </span>
                    <span className="min-width-zero">
                      <span className="d-block font-weight-bold" style={{ color: "#172554" }}>
                        {option.label}
                      </span>
                      <span className="d-block text-muted text-small">{option.description}</span>
                    </span>
                    {selected ? (
                      <i className="simple-icon-check ml-auto text-primary" aria-hidden="true" />
                    ) : null}
                  </button>
                </Col>
              );
            })}
          </Row>

          {selectedCreateOption?.requiresTitle ? (
            <FormGroup className="mb-0 mt-2">
              <Label for="custom-voucher-title" className="font-weight-bold">
                Tiêu đề phiếu <span className="text-danger">*</span>
              </Label>
              <Input
                id="custom-voucher-title"
                value={customVoucherTitle}
                autoFocus
                maxLength={150}
                placeholder="Ví dụ: Chi phí sửa chữa máy lạnh"
                onChange={(event) => setCustomVoucherTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") createSelectedVoucher();
                }}
              />
            </FormGroup>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeCreatePicker}>
            Hủy
          </Button>
          <Button
            color="primary"
            disabled={
              !selectedCreateOption ||
              (selectedCreateOption.requiresTitle && !customVoucherTitle.trim())
            }
            onClick={createSelectedVoucher}
          >
            Tiếp tục
            <i className="simple-icon-arrow-right ml-2" />
          </Button>
        </ModalFooter>
      </Modal>

      <AddIncome
        title={<IntlMessages id="income.add-new-modal-title" />}
        isOpen={show.addIncome}
        toggle={closeModals}
        data_dynamic_form={dynamicForms[CASH_FLOW_TYPE.INCOME]}
        values={form}
        setting={modalSetting}
        onChange={onChangeForm}
        onChangeDynamicForm={onChangeDynamicForm}
        onSubmit={(event) => submitCreate(event, CASH_FLOW_TYPE.INCOME)}
      />
      <UpdateIncome
        title={<IntlMessages id="income.title" />}
        isOpen={show.updateIncome}
        toggle={closeModals}
        data_dynamic_form={dynamicForms[CASH_FLOW_TYPE.INCOME]}
        values={form}
        setting={modalSetting}
        onChange={onChangeForm}
        onChangeDynamicForm={onChangeDynamicForm}
        onSubmit={(event) => submitUpdate(event, CASH_FLOW_TYPE.INCOME)}
      />
      <AddOutcome
        title={<IntlMessages id="outcome.add-new-modal-title" />}
        isOpen={show.addOutcome}
        toggle={closeModals}
        data_dynamic_form={dynamicForms[CASH_FLOW_TYPE.OUTCOME]}
        values={form}
        setting={modalSetting}
        onChange={onChangeForm}
        onChangeDynamicForm={onChangeDynamicForm}
        onSubmit={(event) => submitCreate(event, CASH_FLOW_TYPE.OUTCOME)}
      />
      <UpdateOutcome
        title={<IntlMessages id="outcome.title" />}
        isOpen={show.updateOutcome}
        toggle={closeModals}
        data_dynamic_form={dynamicForms[CASH_FLOW_TYPE.OUTCOME]}
        values={form}
        setting={modalSetting}
        onChange={onChangeForm}
        onChangeDynamicForm={onChangeDynamicForm}
        onSubmit={(event) => submitUpdate(event, CASH_FLOW_TYPE.OUTCOME)}
      />
    </div>
  );
};

const mapStateToProps = ({ searchInput }) => {
  const { keyword: searchKeyword } = searchInput;
  return { searchKeyword };
};

export default connect(mapStateToProps, {
  setSearchContext,
})(CashFlowPage);
